import type { CSSProperties, ReactElement, ReactNode } from 'react'
import { cn } from '@/lib/utils'
import { stageColorByPosition } from '@/lib/stages'

export interface LumenPillProps {
  /** Cor do pill — uma `var(--…)`. Sem cor, cai no cinza neutro. */
  color?: string
  /** Ponto pulsante: o processo está em curso. */
  live?: boolean
  /** Mostrar o ponto. Ligado por omissão quando há cor. */
  dot?: boolean
  children: ReactNode
  className?: string
}

/**
 * LumenPill — pill cuja cor É informação (LUMEN.md §9).
 *
 * Difere do `Pill` do shadcn (variants fixas) por aceitar qualquer cor: os
 * estágios do funil formam um espectro contínuo, e um enum de 4 variants não
 * consegue exprimir isso. Tudo deriva da cor por `color-mix`, por isso um só
 * valor gera o trio coerente nos dois mundos: o fundo e a aresta são o tom
 * puro, e o texto é o tom misturado com o `--ink` do mundo (`--lpill-tom`), para
 * se ler em qualquer superfície (#515).
 */
export function LumenPill({
  color,
  live = false,
  dot,
  children,
  className,
}: LumenPillProps): ReactElement {
  const showDot = dot ?? color !== undefined
  return (
    <span
      className={cn('lpill', live && 'lpill-live', className)}
      style={{ '--pc': color } as CSSProperties}
    >
      {showDot ? <span aria-hidden="true" className="dot" /> : null}
      {children}
    </span>
  )
}

export interface StagePillProps {
  /**
   * O NOME da etapa vigente, como o processo o escreve. `null` em quem ainda
   * não tem etapa — o pill mostra "—" e fica no cinza neutro.
   *
   * Onda W5: era um enum de quatro valores que este componente traduzia para
   * pt-BR. As etapas passaram a ser as do processo escolhido pela vaga, com
   * nomes livres — não há o que traduzir, e traduzir seria inventar.
   */
  name: string | null
  /**
   * A posição da etapa no seu processo. É daqui que sai a cor: o espectro nunca
   * dependeu dos NOMES das etapas, dependia da ordem. Ver `lib/stages.ts`.
   */
  position: number | null
  className?: string
}

/**
 * StagePill — a etapa vigente de uma candidatura, no seu lugar do espectro.
 *
 * Sem `live`: o ponto pulsante marcava as duas etapas do meio do enum antigo
 * ("em entrevista", "em prova"), e num processo de nomes livres não há forma de
 * saber quais são "trabalho vivo" sem adivinhar. O que continua a distinguir
 * quem anda de quem parou é o STATUS, que tem pill próprio ao lado.
 */
export function StagePill({ name, position, className }: StagePillProps): ReactElement {
  return (
    <LumenPill color={stageColorByPosition(position)} {...(className ? { className } : {})}>
      {name ?? '—'}
    </LumenPill>
  )
}
